<template>
  <div class="flex items-center justify-center min-h-screen bg-theme-bg2 p-4">
    <div class="w-full max-w-sm">
      <div class="bg-theme-card border border-theme-border rounded-2xl overflow-hidden">
        <!-- Accent bar -->
        <div class="h-1 bg-gradient-to-r from-primary-600 to-secondary-500"></div>

        <div class="p-6 sm:p-8">
          <div class="flex justify-center items-center mb-5">
            <img :src="isDark ? darkLogo : lightLogo" alt="Company Logo" class="h-10 w-auto" loading="eager" width="160" height="40">
          </div>
          <div class="text-center mb-7">
            <h1 class="text-2xl font-bold text-theme-text">Welcome back</h1>
            <p class="text-sm text-theme-muted mt-1">Sign in to your account to continue</p>
          </div>

          <!-- Error Message -->
          <div v-if="localLoginError" class="bg-red-500/10 border border-red-500/30 rounded-xl p-3.5 mb-5 flex items-start gap-2.5" role="alert" aria-live="assertive">
            <i class="pi pi-exclamation-triangle text-red-500 text-sm mt-0.5 flex-shrink-0"></i>
            <p class="text-red-600 dark:text-red-400 text-sm">{{ localLoginError }}</p>
          </div>

      <form @submit.prevent="handleLogin" novalidate>
        <!-- Email Field -->
        <div class="mb-4">
          <label for="email" class="block text-sm font-medium text-theme-text2 mb-1">
            Email Address
            <span class="text-red-500">*</span>
          </label>
          <input type="email" id="email" v-model.trim="email" @input="validateEmail" @blur="validateEmail"
            placeholder="user@example.com" required autocomplete="email" :class="[
              'w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-1 bg-theme-input text-theme-input-text',
              emailError ? 'border-red-500 focus:ring-red-500' : 'border-theme-input-border focus:border-primary-600 focus:ring-primary-600'
            ]" aria-describedby="email-error" />
          <p v-if="emailError" id="email-error" class="mt-1 text-sm text-red-600">
            {{ emailError }}
          </p>
        </div>

        <!-- Password Field -->
        <div class="mb-6">
          <label for="password" class="block text-sm font-medium text-theme-text2 mb-1">
            Password
            <span class="text-red-500">*</span>
          </label>
          <input type="password" id="password" v-model.trim="password" @input="validatePassword"
            @blur="validatePassword" placeholder="••••••••" required minlength="12" autocomplete="current-password"
            :class="[
              'w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-1 bg-theme-input text-theme-input-text',
              passwordError ? 'border-red-500 focus:ring-red-500' : 'border-theme-input-border focus:border-primary-600 focus:ring-primary-600'
            ]" aria-describedby="password-error" />
          <p v-if="passwordError" id="password-error" class="mt-1 text-sm text-red-600">
            {{ passwordError }}
          </p>
        </div>

        <!-- Submit Button -->
        <button type="submit" :disabled="loading" :class="[
          'w-full py-2.5 px-4 rounded-md font-medium transition-colors duration-200 flex items-center justify-center',
          loading || emailError || passwordError
            ? 'bg-primary-400 text-white cursor-not-allowed'
            : 'bg-primary-600 text-white hover:bg-primary-700 focus:ring-2 focus:ring-primary-600'
        ]" aria-live="polite">
          <i v-if="loading" class="pi pi-spinner pi-spin mr-2"></i>
          <span>{{ loading ? 'Authenticating...' : 'Login' }}</span>
        </button>
      </form>

      <!-- Links Section -->
      <div class="mt-6 space-y-3 text-center">
        <p class="text-sm">
          <router-link to="/password-reset-request"
            class="text-secondary-500 font-semibold hover:underline focus:outline-none focus:underline">
            Forgot Password?
          </router-link>
        </p>
        <p class="text-sm font-semibold text-theme-text2">
          Don't have an account?
          <router-link :to="{ name: 'signup' }"
            class="text-secondary-500 hover:underline focus:outline-none focus:underline">
            Sign up
          </router-link>
        </p>
      </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '@/store/auth';
import { useCartStore } from '@/store/cart';
import { useTheme } from '@/composables/useTheme.js';
import useNotifications from '@/store/notification';
import darkLogo from '@/assets/images/dark_logo.png';
import lightLogo from '@/assets/images/logo.jpeg';
import { useLogger } from '@/composables/useLogger';

const logger = useLogger('Login');

// Constants
const MIN_PASSWORD_LENGTH = 8;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Theme
const { isDark } = useTheme();

// Stores and Services
const { showNotification } = useNotifications();
const authStore = useAuthStore();
const cartStore = useCartStore();
const router = useRouter();

// Reactive State
const email = ref('');
const password = ref('');
const emailError = ref('');
const passwordError = ref('');
const localLoginError = ref('');
const loading = ref(false);



// Validation Functions
const validateEmail = () => {
  if (!email.value) {
    emailError.value = 'Email is required';
  } else if (!EMAIL_REGEX.test(email.value)) {
    emailError.value = 'Please enter a valid email address';
  } else {
    emailError.value = '';
  }
};

const validatePassword = () => {
  if (!password.value) {
    passwordError.value = 'Password is required';
  } else if (password.value.length < MIN_PASSWORD_LENGTH) {
    passwordError.value = `Password must be at least ${MIN_PASSWORD_LENGTH} characters`;
  } else {
    passwordError.value = '';
  }
};

const handleLogin = async () => {
  // Validate before submission
  validateEmail();
  validatePassword();

  if (emailError.value || passwordError.value) {
    return;
  }

  loading.value = true;
  localLoginError.value = '';

  try {
    // 1️⃣ Just login (no cart param needed here)
    await authStore.login(email.value, password.value);

    // 2️⃣ If login succeeded, sync guest cart → backend cart
    if (authStore.isAuthenticated) {
      await cartStore.syncGuestCartToBackend();
    }

    // 3️⃣ Redirect after login
    if (authStore.user?.is_onboarded === false) {
      // New user — send to onboarding first
      authStore.redirectTo = '';
      router.push({ name: 'onboarding' });
    } else {
      const redirectPath =
        authStore.redirectTo && authStore.redirectTo !== ''
          ? authStore.redirectTo
          : { name: 'home' };
      authStore.redirectTo = '';
      router.push(redirectPath);
    }
  } catch (error) {
    logger.error('Login error', error);
    localLoginError.value = error.message || 'Login failed. Please try again.';

    // Clear sensitive data on error
    password.value = '';
    passwordError.value = '';
  } finally {
    loading.value = false;
  }
};

</script>